// Documentation: https://rucsoundings.noaa.gov/raob_format.html
import { CoordinatesGslError, GslError } from "./errors.js";
export var WindUnits;
(function (WindUnits) {
    WindUnits["KT"] = "kt";
    WindUnits["MS"] = "ms";
})(WindUnits || (WindUnits = {}));
export var Sonde;
(function (Sonde) {
    Sonde[Sonde["TypeA"] = 10] = "TypeA";
    Sonde[Sonde["TypeC"] = 11] = "TypeC";
    Sonde[Sonde["SpaceDataCorp"] = 12] = "SpaceDataCorp";
})(Sonde || (Sonde = {}));
export * from "./errors.js";
export default function parseReports(asciiReports) {
    const rawReports = asciiReports.split(/(\n[\s]*\n)/).filter((r) => r.trim());
    const parsedReports = rawReports.map(parseReport).filter(notEmpty);
    if (!parsedReports.length)
        throw new GslError("Malformed GSL file");
    return parsedReports;
}
function parseReport(asciiReport) {
    const lines = asciiReport.split("\n");
    const headerLine = lines.shift();
    const dateLine = lines.shift();
    const capeCinLine = lines.shift();
    if (!headerLine || !dateLine || !capeCinLine)
        return;
    const { type, date } = parseDateLine(dateLine);
    const { cape, cin } = parseCapeCinLine(capeCinLine);
    const data = parseLines(lines);
    if (data.data.length === 1 && data.data[0].dewpt == null)
        throw new CoordinatesGslError();
    return { headerLine, date, type, cape, cin, ...data };
}
function parseCapeCinLine(capeCinLine) {
    const parsed = capeCinLine.split(/[ ]+/).filter((s) => s !== "");
    return { cape: +parsed[1], cin: +parsed[3] };
}
function parseDateLine(dateLine) {
    const parsed = dateLine.split(/[ ]+/).filter((s) => s !== "");
    try {
        var date = new Date(Date.UTC(+parsed[4], getMonth(parsed[3]), +parsed[2], +parsed[1])).toISOString();
    }
    catch (error) {
        throw new GslError("Failed to parse date line", error);
    }
    return {
        type: parsed[0],
        date,
    };
}
function getMonth(mon) {
    return new Date(Date.parse(mon + " 1, 2012")).getMonth();
}
var RapLineTypes;
(function (RapLineTypes) {
    RapLineTypes[RapLineTypes["Identification"] = 1] = "Identification";
    RapLineTypes[RapLineTypes["SoundingCheck"] = 2] = "SoundingCheck";
    RapLineTypes[RapLineTypes["StationID"] = 3] = "StationID";
    RapLineTypes[RapLineTypes["MandatoryLevel"] = 4] = "MandatoryLevel";
    RapLineTypes[RapLineTypes["SignificantLevel"] = 5] = "SignificantLevel";
    RapLineTypes[RapLineTypes["WindLevel"] = 6] = "WindLevel";
    RapLineTypes[RapLineTypes["TropopauseLevel"] = 7] = "TropopauseLevel";
    RapLineTypes[RapLineTypes["MaximumWindLevel"] = 8] = "MaximumWindLevel";
    RapLineTypes[RapLineTypes["SurfaceLevel"] = 9] = "SurfaceLevel";
})(RapLineTypes || (RapLineTypes = {}));
function parseLines(lines) {
    const parsedLines = lines.map((l) => l.split(/[ ]+/).filter((s) => s !== ""));
    const identificationLine = parsedLines.find((l) => +l[0] === RapLineTypes.Identification);
    const stationIdLine = parsedLines.find((l) => +l[0] === RapLineTypes.StationID);
    if (!identificationLine || !stationIdLine)
        throw new GslError("Could not find identification lines for report");
    const identificationData = parseIdentificationLine(identificationLine);
    const stationIdData = parseStationIdLine(stationIdLine);
    const dataLines = parsedLines.filter((l) => +l[0] === RapLineTypes.MandatoryLevel ||
        +l[0] === RapLineTypes.SignificantLevel ||
        +l[0] === RapLineTypes.SurfaceLevel);
    const data = parseDataLines(dataLines);
    return { ...identificationData, ...stationIdData, data };
}
function parseIdentificationLine(line) {
    // Sometimes there is no space between lat and lon.
    // For example: 34.50-135.50 or -25.00-135.00
    // So it need to get parsed out manually.
    if (isNaN(+line[3])) {
        const split = line[3].split("-");
        const lastPart = split.pop();
        const firstPart = split.join("-");
        line.splice(3, 1, firstPart, `-${lastPart}`);
    }
    const [_, wban, wmo, lat, lon, elev, rtime] = line;
    return parseToNumber({
        wban,
        wmo,
        lat,
        lon,
        elev,
        rtime,
    });
}
function parseStationIdLine([_, stationId, sonde, windUnits,]) {
    return {
        stationId,
        sonde: +sonde,
        windUnits: windUnits,
    };
}
function parseDataLines(parsedDataLines) {
    return parsedDataLines.map(parseDataLine).filter(filterInvalidDataLine);
}
function parseDataLine([_, pressure, height, temp, dewpt, windDir, windSpd,]) {
    let parsed = parseToNumber({
        pressure,
        height,
        temp,
        dewpt,
        windDir,
        windSpd,
    });
    if (isInvalidDewpt(parsed.dewpt))
        return { ...parsed, dewpt: undefined };
    return parsed;
}
// https://github.com/NOAA-GSL/GSL-Regional-Model-Forum/discussions/36
// Filter out dewpoints above 100°C
function isInvalidDewpt(dewpt) {
    return dewpt > 1000;
}
function parseToNumber(data) {
    const ret = {};
    Object.keys(data).forEach((key) => {
        ret[key] = +data[key];
    });
    return ret;
}
// 99999 is a test data line (invalid)
function filterInvalidDataLine(dataLine) {
    return dataLine.height !== 99999;
}
function notEmpty(value) {
    return value !== null && value !== undefined;
}
